import { RATE_LIMIT } from '@/utils/enums'

type StorageKeys =
  | 'TOKEN'
  | 'RANDOM_ID'
  | 'USER'
  | 'USERID'
  | 'USERNAME'
  | 'REFRESH_TOKEN'
  | 'EXPIRE_IN'
  | 'utmParams'
  | 'user'
  | 'APPLICATION_DETAILS'
  | RATE_LIMIT.START_TIME

/**
 * StorageService: A TypeScript service for managing browser's localStorage.
 */
class StorageService {
  /**
   * Check if localStorage is available.
   *
   * @returns true if localStorage is available, false otherwise.
   */
  private isLocalStorageAvailable(): boolean {
    try {
      // First check if we're in a browser environment
      if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return false
      }

      const testKey = 'test'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch (error) {
      console.error('localStorage is not available:', error)
      return false
    }
  }

  /**
   * Store a value in localStorage.
   *
   * @param key - The key under which the value should be stored.
   * @param value - The value to be stored.
   */
  set(key: string, value: string | number | boolean | object): void {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot set value for key:', key)
      return
    }
    try {
      // Handle string representations of null/undefined - also remove the key
      if (typeof value === 'string' && (value === 'null' || value === 'undefined')) {
        console.error(`Error setting localStorage key '${key}':`, new Error('Invalid value'))
        return
      }
      // If value is already a string, store it directly
      if (typeof value === 'string') {
        localStorage.setItem(key, value)
      } else {
        // For numbers, booleans, and objects, stringify them
        localStorage.setItem(key, JSON.stringify(value))
      }
    } catch (error) {
      console.error(`Error setting localStorage key '${key}':`, error)
    }
  }

  /**
   * Retrieve a value from localStorage.
   *
   * @param key - The key of the stored value.
   * @returns The retrieved value or undefined if the key doesn't exist.
   */
  get(key: string): string | number | boolean | object | undefined | null {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot get value for key:', key)
      return undefined
    }

    try {
      const item = localStorage.getItem(key)
      if (item === 'null' || !item || item === null || item === 'undefined' || item === undefined) {
        return undefined
      }

      // Try to parse as JSON, if it fails return as string
      try {
        return JSON.parse(item)
      } catch (parseError) {
        // If JSON parsing fails, it's a plain string
        return item
      }
    } catch (error) {
      console.error(`Error getting localStorage key '${key}':`, error)
      return undefined
    }
  }

  /**
   * Remove a key-value pair from localStorage.
   *
   * @param key - The key of the value to be removed.
   */
  remove(key: string): void {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot remove key:', key)
      return
    }

    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`Error removing localStorage key '${key}':`, error)
    }
  }

  /**
   * Clear all keys from localStorage.
   */
  clear(): void {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot clear storage')
      return
    }

    try {
      localStorage.clear()
    } catch (error) {
      console.error('Error clearing localStorage:', error)
    }
  }

  /**
   * Get all keys from localStorage.
   *
   * @returns An array of all keys in localStorage.
   */
  keys(): string[] {
    if (!this.isLocalStorageAvailable()) {
      console.warn('localStorage is not available, cannot get keys')
      return []
    }

    try {
      const keys: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key !== null) {
          keys.push(key)
        }
      }
      return keys
    } catch (error) {
      console.error('Error getting localStorage keys:', error)
      return []
    }
  }
}

// Example Usage
const storage = new StorageService()

export default storage
